import { useMemo, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Plus, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import type { Sip } from '@sbclite/core';
import { PageHeader, StatsGrid, StatCard, FilterBar, ItemCard, ItemCardIcon, ItemActions, EmptyState, LoadingState, ErrorState, DeleteConfirmModal, PageContainer, TypeBadge } from '../../components/common';
import { SipProfileEditor } from '../../components/sip/sip-profile-editor/SipProfileEditor';
import { useDeleteSipProfile, useRunSipProfileBackfill, useSipProfileInventory, useSipProfiles, type SipProfileBackfillResponse } from '../../hooks/useSipProfiles';
import { useViewMode } from '../../hooks/useViewMode';

type FilterType = 'all' | 'enabled' | 'disabled';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Profiles' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

function InventoryPanel() {
  const inventoryQuery = useSipProfileInventory();
  const backfillMutation = useRunSipProfileBackfill();
  const [confirmApply, setConfirmApply] = useState(false);
  const [lastResult, setLastResult] = useState<SipProfileBackfillResponse | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  const summary = inventoryQuery.data?.summary;
  const blockers = summary?.blockers.flatMap((entry) => entry.blockers) ?? [];

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Inventory & backfill</h3>
          <p className="text-sm text-gray-600">Dry-run or apply the SIP profile backfill and review cleanup blockers.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="rounded-md border border-gray-300 px-3 py-2 text-sm" onClick={() => inventoryQuery.refetch()} disabled={inventoryQuery.isFetching}>
            <RefreshCw className={`mr-2 inline h-4 w-4 ${inventoryQuery.isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button type="button" className="rounded-md border border-blue-300 px-3 py-2 text-sm text-blue-700" onClick={async () => {
            try {
              setBackfillError(null);
              setLastResult(await backfillMutation.mutateAsync(false));
            } catch (error) {
              setBackfillError(error instanceof Error ? error.message : 'Dry-run failed');
            }
          }} disabled={backfillMutation.isPending}>
            Dry-run
          </button>
          <button type="button" className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50" disabled={!confirmApply || backfillMutation.isPending} onClick={async () => {
            try {
              setBackfillError(null);
              setLastResult(await backfillMutation.mutateAsync(true));
            } catch (error) {
              setBackfillError(error instanceof Error ? error.message : 'Backfill failed');
            }
          }}>
            Apply backfill
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={confirmApply} onChange={(event) => setConfirmApply(event.target.checked)} />
        I understand backfill will attach only representable legacy policy and will remain blocked when references are missing.
      </label>

      {summary ? (
        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Ready for cleanup</div><div className={`text-lg font-semibold ${summary.readyForCleanup ? 'text-green-700' : 'text-amber-700'}`}>{summary.readyForCleanup ? 'Yes' : 'No'}</div></div>
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Backfillable</div><div className="text-lg font-semibold text-blue-700">{summary.backfillable}</div></div>
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Blocked</div><div className="text-lg font-semibold text-red-700">{summary.blocked}</div></div>
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Enabled</div><div className="text-lg font-semibold text-gray-900">{summary.totals.enabled}</div></div>
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3">
          <div className="font-medium text-red-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Cleanup blockers</div>
          <ul className="space-y-1 text-sm text-red-900">
            {blockers.map((blocker, index) => <li key={`${blocker.code}-${index}`}>{String(blocker.code)} — {String(blocker.message)}</li>)}
          </ul>
        </div>
      ) : null}

      {lastResult ? (
        <div className="space-y-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <div className="font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Last backfill result</div>
          <div>{lastResult.dryRun ? 'Dry-run' : 'Applied'} — {lastResult.appliedCount} peer(s) processed, {lastResult.createdProfileCount} profile(s) created, {lastResult.reusedProfileCount} reused. Cleanup ready: {lastResult.cleanupReady ? 'yes' : 'no'}.</div>
        </div>
      ) : null}

      {backfillError ? <div className="text-sm text-red-700">{backfillError}</div> : null}

      {inventoryQuery.error ? <div className="text-sm text-red-700">Failed to load inventory.</div> : null}
    </div>
  );
}

export default function SipProfilesPage() {
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Sip.SipProfileInfo | null>(null);
  const [profileToDelete, setProfileToDelete] = useState<Sip.SipProfileInfo | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const profilesQuery = useSipProfiles();
  const deleteMutation = useDeleteSipProfile();
  const profiles = profilesQuery.data?.data ?? [];

  const filteredProfiles = useMemo(() => profiles.filter((profile) => {
    const matchesFilter = filterType === 'all' ? true : filterType === 'enabled' ? profile.enabled : !profile.enabled;
    const matchesSearch = searchQuery === '' || profile.name.toLowerCase().includes(searchQuery.toLowerCase()) || (profile.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    return matchesFilter && matchesSearch;
  }), [profiles, filterType, searchQuery]);

  const stats = {
    total: profiles.length,
    enabled: profiles.filter((profile) => profile.enabled).length,
    mandatory: profiles.filter((profile) => profile.srtpMode === 'mandatory').length,
  };

  if (profilesQuery.isLoading) return <LoadingState />;
  if (profilesQuery.error) return <ErrorState title="Failed to load SIP profiles" message={profilesQuery.error instanceof Error ? profilesQuery.error.message : 'Unknown error'} onRetry={() => profilesQuery.refetch()} />;

  return (
    <PageContainer>
      <PageHeader
        title="SIP Profiles"
        description="Manage centralized SIP policy profiles for peers"
        onRefresh={() => profilesQuery.refetch()}
        isRefreshing={profilesQuery.isFetching}
        primaryAction={{ label: 'Create Profile', icon: Plus, onClick: () => { setEditingProfile(null); setShowEditor(true); } }}
      />

      <StatsGrid>
        <StatCard label="Total Profiles" value={stats.total} icon={Users} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard label="Enabled" value={stats.enabled} icon={ShieldCheck} iconBgColor="bg-green-100" iconColor="text-green-600" valueColor="text-green-600" />
        <StatCard label="Mandatory SRTP" value={stats.mandatory} icon={Activity} iconBgColor="bg-purple-100" iconColor="text-purple-600" valueColor="text-purple-600" />
      </StatsGrid>

      <InventoryPanel />

      <FilterBar
        searchPlaceholder="Search profiles..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {filteredProfiles.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No SIP profiles found"
          description="Create a profile to centralize SIP policy for attached peers."
          isFiltered={searchQuery !== '' || filterType !== 'all'}
          action={{ label: 'Create Profile', icon: Plus, onClick: () => { setEditingProfile(null); setShowEditor(true); } }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {filteredProfiles.map((profile) => (
            <ItemCard
              key={profile.id}
              icon={<ItemCardIcon icon={<ShieldCheck className={`h-5 w-5 ${profile.enabled ? 'text-blue-600' : 'text-gray-400'}`} />} bgColor={profile.enabled ? 'bg-blue-100' : 'bg-gray-100'} />}
              title={profile.name}
              subtitle={profile.description || 'SIP policy profile'}
              status={<TypeBadge label={profile.enabled ? 'Enabled' : 'Disabled'} variant={profile.enabled ? 'green' : 'gray'} size="sm" />}
              details={[
                { label: 'Session timers', value: profile.sessionTimerMode },
                { label: 'Registrar', value: profile.registrarEnabled ? 'On' : 'Off' },
                { label: 'Digest', value: profile.digestAuthEnabled ? 'On' : 'Off' },
              ]}
              tags={<div className="flex flex-wrap gap-1">{profile.cryptoSuites.slice(0, 3).map((suite) => <TypeBadge key={suite} label={suite} variant="gray" size="sm" />)}</div>}
              actions={<ItemActions variant="card" actions={[{ kind: 'edit', onClick: () => { setEditingProfile(profile); setShowEditor(true); } }, { kind: 'delete', onClick: () => { setProfileToDelete(profile); setShowDeleteModal(true); } }]} />}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Profile</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Policy</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Crypto suites</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredProfiles.map((profile) => (
                <tr key={profile.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{profile.name}</div>
                    <div className="text-sm text-gray-500">{profile.description || '—'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div>Session timers: {profile.sessionTimerMode}</div>
                    <div>Registrar: {profile.registrarEnabled ? 'on' : 'off'}</div>
                    <div>Digest: {profile.digestAuthEnabled ? 'on' : 'off'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{profile.cryptoSuites.join(', ')}</td>
                  <td className="px-6 py-4 text-right">
                    <ItemActions variant="table" actions={[{ kind: 'edit', onClick: () => { setEditingProfile(profile); setShowEditor(true); } }, { kind: 'delete', onClick: () => { setProfileToDelete(profile); setShowDeleteModal(true); } }]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SipProfileEditor
        isOpen={showEditor}
        onClose={() => { setShowEditor(false); setEditingProfile(null); }}
        onSuccess={() => profilesQuery.refetch()}
        editingProfile={editingProfile}
      />

      {profileToDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setProfileToDelete(null); }}
          onConfirm={() => deleteMutation.mutate(profileToDelete.id, { onSuccess: () => { setShowDeleteModal(false); setProfileToDelete(null); profilesQuery.refetch(); } })}
          title="Delete SIP Profile"
          entityType="profile"
          entityName={profileToDelete.name}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </PageContainer>
  );
}
